import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { openClickToDial } from "data/user/actions";
import css from "./style.scss";
import phoneicon from "../../../../assets/images/phoneicon.svg"
import phoneiconhover from "../../../../assets/images/phone-white.svg"
import blockphone from "../../../../assets/images/block-phone.svg"
import bluetick from "../../../../assets/images/bluetick.svg"
import ResettableAutocompleteDropdown from "../../../../components/SearchDropdown/ResettableAutocompleteDropdown";
import { formatPhone } from "data/user/constants";
import Tooltip from "../../../components/Tooltip";
import SkipTraceButton from "../SkipTraceButton";
import skipTraceIcon from "../../../../assets/images/campaigns/dialer-campaign-skip-trace-icon.svg";
import noDataIcon from "../../../../assets/images/campaigns/contact-has-no-data-icon.svg";
import { saveDNCStatusForAccountPhoneNumbers } from 'data/contacts/actions';
import CurrentLeadsDropdown from "../../../../components/CurrentLeadsDropdown";
import { isRedactedNumber } from "../../../../utils/contactUtil";

const statusOptions = [
  { id: 1, name: "No Status" },
  { id: 2, name: "My DNC" },
];

const getDefaultStatus = (phone) => {
  if (phone.myDNC === true) {
    return statusOptions.find(opt => opt.name === "My DNC");
  }
  else if (phone.myDNC === false) {
    return statusOptions.find(opt => opt.name === "No Status");
  }
  return null;
}

const CurrentLeadContactCard = ({
  lead,
  dialerCampaignId,
  marketingCampaignId,
  accountId,
}) => {
  const dispatch = useDispatch();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!lead) return null;
  const contactInfo = lead.contactInfo || {};
  const phoneNumbers = contactInfo?.phoneNumbers || [];
  const hasPhoneNumbers = phoneNumbers?.length > 0;
  const contactId = contactInfo?.id;
  const isSkipTraced = !!lead.contactInfo?.skipTraced;
  const isNoDataState = isSkipTraced && !hasPhoneNumbers;
  const isLitigator = !!contactInfo.litigator;

  // No Data state: skipTraced === true AND no phone numbers
  if (isNoDataState) {
    return (
      <div className={`${css.contactCard} ${css.emptyState}`}>

        {/* Card content */}
        <div className={css.emptyStateCard}>
          <div className={css.emptyStateHeader}>
            <div className={css.contactHeader}>
              {contactInfo?.name ? (
                <>
                  <span className={css.avatar}>{contactInfo.name.slice(0, 2).toUpperCase()}</span>
                  <span className={css.name}>{contactInfo.name}</span>
                </>
              ) : (
                <>
                  <span className={css.avatar}>—</span>
                  <span className={css.name}>—</span>
                </>
              )}
            </div>
          </div>

          {/* Illustration */}
          <div className={css.emptyStateIllustration}>
            <img src={noDataIcon} alt="No Contact Data" />
          </div>

          {/* Message */}
          <div className={css.emptyStateMessage}>
            Sorry, no contact information available.
          </div>
        </div>

      </div>
    );
  }

  // Not skip traced state: skipTraced === false
  else if (!isSkipTraced) {
    return (
      <div className={`${css.contactCard} ${css.emptyState}`}>

        {/* Card content */}
        <div className={css.emptyStateCard}>
          <div className={css.emptyStateHeader}>
            <div className={css.contactHeader}>
              <span className={css.avatar}>{contactInfo.name?.slice(0, 2).toUpperCase()}</span>
              <span className={css.name}>{contactInfo.name}</span>
            </div>
            <div className={css.skipTraceButtonContainer}>
              <SkipTraceButton contactIds={contactId ? [contactId] : []} />
            </div>
          </div>

          {/* Illustration */}
          <div className={css.emptyStateIllustration}>
            <img src={skipTraceIcon} alt="Skip Trace" />
          </div>

          {/* Message */}
          <div className={css.emptyStateMessage}>
            You don't have any numbers available yet. Skip Trace this<br />
            lead for free to start making calls directly from PropStream.
          </div>
        </div>

      </div>
    );
  }

  const getInitials = (name = "") => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleCallClick = (phone) => {

    dispatch(openClickToDial({
      contactId: contactInfo.id,
      phonenumber: phone.phoneNumber,
      myDnc: phone.myDNC,
      publicDnc: phone.publicDNC,
      litigatorFlag: contactInfo.litigator,
      dialerCampaignId,
      marketingCampaignId,
      accountId,
      name: contactInfo.name,
      intials: getInitials(contactInfo.name),
    }));
  };
  return (
    <div className={css.contactCard}>
      <div className={css.container}>
        <div className={css.contactHeader}>
          <span className={css.avatar}>
            {getInitials(contactInfo.name)}
          </span>

          <div className={css.nameWrapper}>
            <span className={css.name}>{contactInfo.name}</span>

            {isLitigator && (
              <Tooltip
                title="Litigator"
                position="bottom"
                description="A litigator is an individual who may be associated with legal actions, such as filing lawsuits related to telemarketing or consumer protection claims."
                width={300}
                xOffset={60}
                arrowOffset={25}
              >
                <span className={css.litigator}>Litigator</span>
              </Tooltip>
            )}
          </div>
        </div>
        {lead.contactInfo.contacted && (
          <Tooltip
            title="Contacted"
            description="A successful connection was made to either the voicemail or the person associated with this phone number."
            position="bottom"
            width={312}
            xOffset={125}
            yOffset={2}
            arrowOffset={30}
            bgColor="#128292"
          >
            <div className={css.contactedHeader}>
              <img src={bluetick} alt="contacted" />
              <span>Contacted</span>
            </div>
          </Tooltip>
        )}
      </div>
      {/* Scrollable list */}
      <div className={css.contactList}>
        {phoneNumbers.map((phone, index) => {
          const isPublicDnc = phone.publicDNC;
          const contactedCount = phone.contactedCount || 0;
          return (
            <div key={index} className={css.contactItem}>
              <div className={css.contactRow}>
                <div className={css.contactNumber}>
                  <span className={css.index}>{index + 1}.</span>
                  <span className={css.phone}>
                    {formatPhone(phone.phoneNumber)}
                  </span>
                  <span className={css.tickSlot}>
                    {contactedCount > 0 && (
                      <Tooltip
                        position="bottom"
                        bgColor="#128292"
                        width={200}
                        xOffset={70}
                        yOffset={2}
                        arrowOffset={30}
                        hideCloseButton
                        content={`Contacted: ${contactedCount} times`}
                      >
                        <img src={bluetick} alt="contacted" />
                      </Tooltip>
                    )}
                  </span>
                </div>

                {isPublicDnc ? (
                  <div className={css.publicDnc}>
                    <img src={blockphone} />
                    <span className={css.dnc}>Public DNC</span>
                  </div>
                ) : (
                  <div className={css.dropdown}>
                    <CurrentLeadsDropdown
                      showLabel={false}
                      items={statusOptions}
                      value={getDefaultStatus(phone)}
                      disabled={isRedactedNumber(phone.phoneNumber)}
                      onSelect={(selected) => {
                        const isMyDNC = selected.id == 2;
                        const payload = {
                          phoneNumbers: [{
                            contactPhoneId: phone.contactPhoneId,
                            phoneNumber: phone.phoneNumber,
                            myDNC: isMyDNC
                          }]
                        };
                        dispatch(saveDNCStatusForAccountPhoneNumbers(payload));

                        phone.myDNC = isMyDNC;
                      }}
                    />
                  </div>
                )}

                <button className={css.callBtn}
                  onClick={() => handleCallClick(phone)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  disabled={isRedactedNumber(phone.phoneNumber)}
                >
                  <img
                    src={hoveredIndex === index ? phoneiconhover : phoneicon}
                    alt="call"
                  />
                  <span>Call</span>
                </button>
              </div>

              {index !== phoneNumbers.length - 1 && (
                <div className={css.contactDivider} />
              )}
            </div>
          );
        })}

      </div>

    </div>
  );
};

export default CurrentLeadContactCard;